import { Theme } from '@infotech-soe/erp-ui-kit';
import {
  TContextMenuItemBadge,
  TDatabaseId,
  TSortQueryDirEnum,
  TUpdaterName,
} from '@infotech-soe/metaschema';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';

import { CategoryOpenMode } from '../../../models/category/category';
import { DatasetColumnWidthConfig } from '../../../models/user-config/DisplayModeConfig';
import CommandViewModel, {
  CommandListEntryType,
} from '../../../services/commands/CommandViewModel';
import { DatasetGridRow } from '../../../services/dataset/view-model/DatasetGridRow';
import {
  getEditableFields,
  getRules,
} from '../../../services/form/builders/controls/buildDatasetInputControl';
import { DatasetFormControlViewModel } from '../../../services/form/view-model/FormControlViewModel';
import UniqueId from '../../../services/uid/UniqueId';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getCurrentLocaleName } from '../../../store/selectors/intl';
import form from '../../../store/slices/form';
import {
  $ROW_HIDDEN_ACTIVE_ID,
  $ROW_HIDDEN_ID,
  FEATURES,
  formGridFeatures,
  ROW_HEIGHT,
} from '../dataset/constants';
import DatasetGrid from '../dataset/DatasetGrid';
import { hiddenFieldsInRows } from '../dataset/utils/hiddenFieldsInRows';
import orderBy from '../dataset/utils/orderBy';

import { TFormControlConnectorProps } from './FormControlContainer';
import FormLabeledInputControl from './FormLabeledInputControl';

interface Props extends Pick<TFormControlConnectorProps, 'areHintsShown'> {
  control: DatasetFormControlViewModel;
  formId: UniqueId;
  onChanged?: (value: DatasetGridRow[]) => void;
  onChange?: (value: DatasetGridRow[]) => void;
  onRunSingleUpdater: (name: TUpdaterName) => void;
}

const getHeight = (rows: number | undefined, { global }: Theme) => {
  const scrollBarSize = global['::-webkit-scrollbar']?.width;
  return ROW_HEIGHT * ((rows ?? 1) + 1) + scrollBarSize;
};

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    borderRadius: theme.sizes.inputContainer.borderRadius,
    height: (rows?: number) => getHeight(rows, theme),
    '&:hover, &:focus-within': {
      borderColor: theme.colors.primary,
    },
  },
  fullHeight: {
    height: '100%',
    minHeight: (rows?: number) => getHeight(rows, theme),
    '& > label': { height: '100%', display: 'flex', flexDirection: 'column' },
    '& > label > div:first-child': { flex: '0 0 auto' },
    '& > label > div:last-child': { flex: '1 1 0', minHeight: 0 },
    '& $container': { height: '100%' },
  },
}));

const FormDatasetInputControl = ({
  areHintsShown,
  control,
  formId,
  onChange,
  onChanged,
  onRunSingleUpdater,
}: Props): React.JSX.Element => {
  const classes = useStyles(control.rows);
  const dispatch = useAppDispatch();

  const [config, setConfig] = useState({ ...control.config });
  const [selectedIds, setSelectedIds] = useState<TDatabaseId[]>([]);
  const [activeRowId, setActiveRowId] = useState<TDatabaseId | null>(null);
  const [sortDirection, setSortDirection] = useState<TSortQueryDirEnum>(
    TSortQueryDirEnum.NO
  );
  const [rows, setRows] = useState<DatasetGridRow[]>([]);

  useEffect(() => {
    setRows(hiddenFieldsInRows.add(control));
  }, [control]);

  useEffect(() => {
    setConfig({
      ...config,
      preset: {
        default: {
          ...config.preset.default,
          rules: getRules(control.projection, control.rules),
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [control.projection, control.rules]);

  const rowsWithoutTotals = useMemo(
    () => rows.slice(0, Math.max(0, rows.length - (control.totals || 0))),
    [rows, control.totals]
  );

  const sortRowsByDirection = useCallback(
    (
      field: keyof DatasetGridRow,
      direction: 'asc' | 'desc',
      totalsRows: DatasetGridRow[]
    ): DatasetGridRow[] => [
      ...rowsWithoutTotals.slice().sort(orderBy([field], [direction])),
      ...totalsRows,
    ],
    [rowsWithoutTotals]
  );

  const applySorting = useCallback(
    (
      field: keyof DatasetGridRow,
      totalsRows: DatasetGridRow[],
      sortDirectionValue: TSortQueryDirEnum,
      directionKey: 'asc' | 'desc'
    ): void => {
      const sortedRows = sortRowsByDirection(field, directionKey, totalsRows);
      onChange?.(sortedRows);
      onChanged?.(sortedRows);
      setSortDirection(sortDirectionValue);
      setConfig({
        ...config,
        sort: [{ field, dir: sortDirectionValue }],
      });
    },
    [config, sortRowsByDirection, onChange, onChanged]
  );

  const handleSortChange = useCallback(
    (field: keyof DatasetGridRow) => {
      const totalsRows = [...rows].slice(control.totals ? -control.totals : 0);
      switch (sortDirection) {
        case TSortQueryDirEnum.NO: {
          applySorting(field, totalsRows, TSortQueryDirEnum.ASC, 'asc');
          break;
        }
        case TSortQueryDirEnum.ASC: {
          applySorting(field, totalsRows, TSortQueryDirEnum.DESC, 'desc');
          break;
        }
        case TSortQueryDirEnum.DESC: {
          const sortedRows = sortRowsByDirection(
            $ROW_HIDDEN_ID,
            'asc',
            totalsRows
          );
          onChange?.(sortedRows);
          onChanged?.(sortedRows);
          setSortDirection(TSortQueryDirEnum.NO);
          setConfig({
            ...config,
            sort: [],
          });
          break;
        }
        default:
          throw new Error('unknown sortDirection');
      }
    },
    [
      applySorting,
      config,
      control.totals,
      rows,
      sortDirection,
      sortRowsByDirection,
      onChange,
      onChanged,
    ]
  );

  const handleMoveColumn = useCallback(
    (order: string[]): void => {
      const newConfig = { ...config, columnsOrder: order };
      dispatch(
        form.updateControlProp({
          formId,
          field: control.name,
          propName: 'config',
          propValue: newConfig,
        })
      );
      setConfig(newConfig);
    },
    [config, formId, control.name, dispatch]
  );

  const handleResizeColumn = useCallback(
    ({ field, width }: DatasetColumnWidthConfig): void => {
      const newConfig = {
        ...config,
        columnsWidth: { ...config.columnsWidth, [field]: width },
      };
      dispatch(
        form.updateControlProp({
          formId,
          field: control.name,
          propName: 'config',
          propValue: newConfig,
        })
      );
      setConfig(newConfig);
    },
    [config, formId, control.name, dispatch]
  );

  const handleChangeSelectedIds = (ids: number[]) => {
    setSelectedIds(ids);
  };

  const handleActiveChange = useCallback(
    (rowId: TDatabaseId): void => {
      if (activeRowId !== rowId) {
        setActiveRowId(rowId);
        const updatedRows = rows.map((row, idx) =>
          idx === 0 ? { ...row, [$ROW_HIDDEN_ACTIVE_ID]: rowId } : row
        );
        onChanged?.(updatedRows);
      }
    },
    [activeRowId, onChanged, rows]
  );

  const locale = useAppSelector(getCurrentLocaleName);

  const handleOnChanged = useCallback(
    (rowsForOnChanged: DatasetGridRow[]): void => {
      onChanged?.(rowsForOnChanged);
      setRows(rowsForOnChanged);
    },
    [onChanged]
  );

  const editableFields = useMemo(
    () => getEditableFields(control.projection),
    [control.projection]
  );

  const commands: CommandViewModel[] = useMemo(() => {
    const result: CommandViewModel[] = [];
    const getBadge = (commandName: string): string | number | undefined => {
      for (const key in control.commands) {
        if (
          Object.hasOwn(control.commands, key) &&
          !control.commands[key].type &&
          key === commandName
        ) {
          const badge = (
            control.commands[key] as { badge?: TContextMenuItemBadge }
          ).badge;
          if (typeof badge === 'boolean') {
            return badge ? '✓' : undefined;
          }
          return badge;
        }
      }
    };

    for (const key in control.commands) {
      if (Object.hasOwn(control.commands, key) && control.commands[key].type) {
        const command = control.commands[key];
        if (command.type === CommandListEntryType.COMMAND) {
          result.push({
            ...command,
            badge: getBadge(command.name),
          });
        } else {
          result.push(command);
        }
      }
    }
    return result;
  }, [control.commands]);

  const features = useMemo((): FEATURES[] => {
    const result: FEATURES[] = formGridFeatures.slice();
    if (commands.length) {
      result.push(FEATURES.CONTEXT_MENU);
    }
    if (!control.disableMoveColumn) {
      result.push(FEATURES.MOVE_COLUMN);
    }
    return result;
  }, [commands, control.disableMoveColumn]);

  return (
    <div className={control.fullHeight ? classes.fullHeight : undefined}>
      <FormLabeledInputControl
        areHintsShown={areHintsShown}
        control={control}
        onClick={e => e.preventDefault()}
      >
        <DatasetGrid
          rows={rows}
          disabled={control.disabled}
          totals={control.totals}
          className={classes.container}
          commands={commands}
          config={config}
          conditions={control.conditions}
          hasConditionsHook={false}
          selectedIds={selectedIds}
          columns={control.columnsData}
          editableFields={editableFields}
          recordViewMap={undefined}
          rowIdField={$ROW_HIDDEN_ID}
          activeRowId={activeRowId}
          datasetName=""
          datasetId={control.name}
          categoryId=""
          datasetTitle=""
          isShowForm={false}
          openMode={CategoryOpenMode.CONTENT}
          locale={locale}
          onChangeSelectedIds={handleChangeSelectedIds}
          onSortChange={handleSortChange}
          onMoveColumn={handleMoveColumn}
          onResizeColumn={handleResizeColumn}
          onActiveChange={handleActiveChange}
          onInitiateCommand={onRunSingleUpdater}
          onInitiateCommandsScheduled={() => null}
          onUpdateConfig={() => null}
          onCloseForm={() => null}
          onChange={onChange}
          onChanged={handleOnChanged}
          onError={() => null}
          features={features}
          fieldsConditionsCount={0}
          labelTitle={control.labelTitle}
          conditionsCount={0}
          withBorders="All"
          activeCategoryId={null}
          isDatasetRoot={false}
        />
      </FormLabeledInputControl>
    </div>
  );
};

export default FormDatasetInputControl;
